<template>
  <v-container fluid class="serial-list-page pa-0">
    <v-card :loading="loading" outlined class="serial-list-card">
      <div class="serial-list-header">
        <div class="title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div>
            <div class="page-title">Serial Inventory</div>
            <div class="page-subtitle">
              {{ count }} serial number{{ count === 1 ? "" : "s" }} found
            </div>
          </div>
        </div>
        <div class="header-actions">
        <v-btn
          outlined
          rounded
          small
          @click="extraCostDialog = true"
        >
          <v-icon left small>mdi-cash-plus</v-icon>Extra Cost
        </v-btn>
        <v-btn rounded small color="primary" dark @click="changeStatusDialog = true">
          <v-icon left small>mdi-swap-horizontal</v-icon>Change Status
        </v-btn>
        </div>
      </div>

      <div class="serial-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search product or serial number"
          single-line
          hide-details
          clearable
          class="serial-search"
        />
          <v-select
            :items="statusList"
            @change="getList()"
            return-object
            item-text="name"
            clearable
            outlined
            dense
            label="Status"
            v-model="selectedStatus"
            hide-details
            class="status-filter"
          />
          <v-spacer></v-spacer>

        <DateTimePicker
          :defaultRange="
            currentBusiness.metadata &&
            currentBusiness.metadata.invoiceViewLimit
              ? currentBusiness.metadata.invoiceViewLimit.range
              : 'since15DaysAgo'
          "
          :range="true"
          :hideTime="true"
          v-model="dateTime"
          @submit="getList()"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-icon primary-soft"><v-icon color="primary">mdi-barcode-scan</v-icon></div>
            <div><div class="summary-label">Current Results</div><div class="summary-value">{{ vendors.length }}</div></div>
          </div>
          <div class="summary-box">
            <div class="summary-icon green-soft"><v-icon color="green darken-2">mdi-check-decagram-outline</v-icon></div>
            <div><div class="summary-label">Selected Status</div><div class="summary-value summary-status">{{ selectedStatus ? selectedStatus.name : "All" }}</div></div>
          </div>
        </div>
        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="vendors"
          :loading="loading"
          :server-items-length="count"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="serial-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer serial-row">
              <td class="text-left">
                {{ item.createdAt | moment("MM/DD/YYYY") }}
              </td>

              <td class="text-left">
                <div class="product-name" v-html="$highlight(item.Product.name, search)"></div>
                <div class="cell-subtitle">Product #{{ item.Product.localId || item.Product.id }}</div>
              </td>

              <td class="text-center">
                <div class="row-actions">
                    <v-btn
                      @click="setOpenSerialSearch(item)"
                      text
                      icon
                      small
                      color="primary"
                    >
                      <v-icon small>mdi-feature-search-outline</v-icon>
                    </v-btn>
                    <v-btn
                      text
                      icon
                      color="primary"
                      @click="copyCode(item.imeiNumber)"
                      small
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                </div>
              </td>

              <td class="text-left">
                <span class="serial-code" v-html="$highlight(item.imeiNumber, search)"></span>
              </td>

              <td class="text-left">
                <v-chip small :class="['status-chip', 'status-' + item.status]">
                  <span class="status-dot"></span>{{ textCapitalize(item.status) }}
                </v-chip>
              </td>

              <td class="text-right">
                {{ item.cost | currency }}
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="52" color="grey lighten-1">mdi-barcode-off</v-icon>
              <div class="empty-title">No serial numbers found</div>
              <div class="cell-subtitle">Try another status, date range, or search.</div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <ExtraSerializedCost
      @close="extraCostDialog = false"
      @submit="getList()"
      :extraCostDialog="extraCostDialog"
    />

    <ChangeStatus
      @close="changeStatusDialog = false"
      @submit="getList()"
      :changeStatusDialog="changeStatusDialog"
    />
  </v-container>
</template>

<script>
import serialService from "@/modules/Inventory/Serial/service.js";
import { mapGetters, mapActions } from "vuex";
import ExtraSerializedCost from "@/modules/Inventory/Serial/ExtraSerializedCost/components/ExtraSerializedCost.vue";
import ChangeStatus from "@/modules/Inventory/Serial/components/ChangeStatus.vue";
const DateTimePicker = () => import("@/components/DateTimePicker");
import _ from "lodash";

export default {
  name: "serial-list",
  data() {
    return {
      selectedStatus: { id: "available", name: "Available" },
      statusList: [
        { id: "available", name: "Available" },
        { id: "sold", name: "Sold" },
        { id: "onHold", name: "On Hold" },
        { id: "repair", name: "Repair" },
      ],
      status: 0,
      createDialog: false,
      dateTime: {
        start: this.$moment()
          .startOf("days")
          .subtract(60, "days")
          .toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      editDialog: false,
      selectedvendor: null,
      changeStatusDialog: false,
      extraCostDialog: false,
      headers: [
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: true,
        }, // CHANGED: sortable true
        {
          text: "Product Name",
          align: "left",
          value: "Product.name",
          sortable: true,
        }, // CHANGED: sortable true (backend supports)
        { text: "", value: "action", sortable: false },
        {
          text: "IMEI Number",
          align: "left",
          value: "imeiNumber",
          sortable: true,
        }, // CHANGED: sortable true
        { text: "Status", align: "left", value: "status", sortable: true },
        { text: "Cost", value: "cost", sortable: true },
      ],

      vendors: [],
      count: 0,
      search: "",
      loading: true,

      // CHANGED: default sortBy to a real field; keep using options.sync for Vuetify server mode
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"], // CHANGED: was ['balance'] which isn't a column here
        sortDesc: [true],
      },
    };
  },
  created() {
    this.getList();
    document.title = "smplsale - Serial List";
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
  },
  components: {
    DateTimePicker,
    ChangeStatus,
    ExtraSerializedCost,
  },
  watch: {
    status() {
      this.getList();
    },
    // Debounced server search; trigger on clear or >=3 chars
    search: _.debounce(function () {
      if (
        this.search === null ||
        this.search.length === 0 ||
        this.search.length > 2
      ) {
        // reset to first page when search changes
        this.options.page = 1;
        this.getList();
      }
    }, 300),
    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("layout", ["setOpenSerialSearch", "setOpenRestockSerial"]),
    copyCode(code) {
      navigator.clipboard.writeText(code);
      this.$swal
        .mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: true,
          timer: 2000,
        })
        .fire({ icon: "success", title: "IMEI number was copy" });
    },
    textCapitalize(val) {
      if (!val) return "Unknown";
      const capitalizedText = val.charAt(0).toUpperCase() + val.slice(1);
      return capitalizedText;
    },
    viewProfile(val) {
      this.$router.push({ path: `/inventory/vendor/${val.id}/profile` });
    },
    add() {
      this.createDialog = !this.createDialog;
    },

    getList() {
      this.loading = true;
      this.changeStatusDialog = false;
      this.extraCostDialog = false;

      const { page, itemsPerPage, sortBy, sortDesc } = this.options;

      // offset calculation
      const limit = itemsPerPage > 0 ? itemsPerPage : 10;
      const offset = page && itemsPerPage > 0 ? (page - 1) * itemsPerPage : 0;

      // Vuetify may provide arrays; normalize to single field/dir
      const sb =
        Array.isArray(sortBy) && sortBy.length ? sortBy[0] : "createdAt";
      const sd =
        Array.isArray(sortDesc) && sortDesc.length ? !!sortDesc[0] : true;

      const where = {
        status: this.selectedStatus?.id,
        start: this.dateTime.start,
        end: this.dateTime.end,
        limit,
        offset,
        sortBy: sb, // <-- backend will map 'Product.name' as needed
        sortDesc: sd,
        // Back-compat for older handlers that expect orderBy[0/1]
        orderBy: [sb, String(sd)],
      };

      if (this.search && (this.search.length === 0 || this.search.length > 2)) {
        where.search = this.search;
      }

      return serialService
        .getAll(where)
        .then((response) => {
          this.loading = false;

          // Your API shape is { count, data } (no .data wrapper)
          this.count = Number(response.count) || 0;
          this.vendors = Array.isArray(response.data) ? response.data : [];

          return response;
        })
        .catch((err) => {
          this.loading = false;
          console.error("serial list load error:", err);
        });
    },
  },
};
</script>

<style scoped>
.serial-list-page{background:#f8fafc}.serial-list-card{border-radius:20px;background:#fff;overflow:hidden}.serial-list-header{padding:16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:16px}.title-wrap,.header-actions,.row-actions{display:flex;align-items:center}.header-actions{gap:8px;flex-wrap:wrap}.page-title{font-size:22px;font-weight:800;color:#0f172a}.page-subtitle,.cell-subtitle{font-size:12px;color:#64748b;margin-top:2px}.serial-filter-bar{padding:12px 16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;gap:12px;flex-wrap:wrap}.serial-search{max-width:390px;min-width:280px}.status-filter{max-width:210px}.summary-row{display:grid;grid-template-columns:repeat(2,minmax(0,240px));gap:12px}.summary-box{background:#f8fafc;border-radius:16px;padding:14px;display:flex;align-items:center;gap:12px}.summary-icon{width:42px;height:42px;border-radius:13px;display:flex;align-items:center;justify-content:center}.primary-soft{background:#eaf2ff}.green-soft{background:#e9f8ef}.summary-label{font-size:12px;color:#64748b}.summary-value{font-size:22px;font-weight:800;color:#0f172a}.summary-status{font-size:17px}.serial-table{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden}.serial-row:hover{background:#f8fafc}.product-name{font-weight:700;color:#0f172a}.serial-code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:700;color:#334155;letter-spacing:.02em}.row-actions{justify-content:center;gap:2px}.status-chip{font-weight:700;border:1px solid transparent}.status-dot{width:7px;height:7px;border-radius:50%;background:currentColor;margin-right:7px}.status-available{background:#ecfdf3!important;color:#15803d!important;border-color:#bbf7d0!important}.status-sold{background:#fff1f2!important;color:#be123c!important;border-color:#fecdd3!important}.status-onHold{background:#fff7ed!important;color:#c2410c!important;border-color:#fed7aa!important}.status-repair{background:#eff6ff!important;color:#1d4ed8!important;border-color:#bfdbfe!important}.empty-state{text-align:center;padding:42px 16px}.empty-title{font-size:17px;font-weight:800;color:#0f172a;margin-top:8px}@media(max-width:700px){.serial-list-header{align-items:flex-start;flex-direction:column}.header-actions{padding-left:40px}.serial-search,.status-filter{max-width:100%;min-width:100%}.summary-row{grid-template-columns:1fr}}
</style>
